export type Locale = "en" | "ar";

interface Course {
  id: string;
  title: string;
  desc: string;
  age: string;
  duration: string;
  level: string;
  color: string;
}
interface Plan {
  name: string;
  price: string;
  period: string;
  features: string[];
  popular?: boolean;
}
interface Feature {
  t: string;
  d: string;
}
interface Step {
  n: string;
  t: string;
  d: string;
}
interface Stat {
  n: string;
  l: string;
}
interface FaqItem {
  q: string;
  a: string;
}
interface Project {
  emoji: string;
  t: string;
  a: string;
}
interface Testimonial {
  name: string;
  role: string;
  q: string;
}
interface EventItem {
  date: string;
  t: string;
  d: string;
}
interface LegalPage {
  title: string;
  body: string;
}

export interface Dict {
  brand: string;
  slogan: string;
  nav: Record<
    | "home"
    | "about"
    | "courses"
    | "path"
    | "instructors"
    | "projects"
    | "pricing"
    | "faq"
    | "contact"
    | "login"
    | "signup"
    | "blog"
    | "events"
    | "resources",
    string
  >;
  cta: Record<"startLearning" | "bookFree" | "explore" | "enroll" | "verify" | "download", string>;
  resources: {
    title: string;
    desc: string;
    bookTitle: string;
    bookDesc: string;
    bookAuthor: string;
    activitiesTitle: string;
    activitiesDesc: string;
    activities: { title: string; desc: string }[];
  };
  home: {
    heroEyebrow: string;
    heroDesc: string;
    trustedBy: string;
    whyTitle: string;
    whyDesc: string;
    featuresList: Feature[];
    coursesTitle: string;
    coursesDesc: string;
    projectsTitle: string;
    projectsDesc: string;
    projectsList: Project[];
    testimonialsTitle: string;
    testimonialsList: Testimonial[];
    eventsTitle: string;
    eventsList: EventItem[];
    faqTitle: string;
    ctaTitle: string;
    ctaDesc: string;
  };
  about: {
    title: string;
    lead: string;
    mission: string;
    missionText: string;
    vision: string;
    visionText: string;
    stats: Stat[];
  };
  courses: {
    title: string;
    desc: string;
    ageLabel: string;
    durationLabel: string;
    levelLabel: string;
    viewDetails: string;
    list: Course[];
  };
  path: { title: string; desc: string; steps: Step[] };
  pricing: {
    title: string;
    desc: string;
    monthly: string;
    term: string;
    yearly: string;
    perMonth: string;
    popular: string;
    plans: Plan[];
  };
  faq: { title: string; items: FaqItem[] };
  contact: {
    title: string;
    desc: string;
    name: string;
    email: string;
    phone: string;
    childAge: string;
    message: string;
    send: string;
    sending: string;
    bookSession: string;
    success: string;
    emailLabel: string;
    phoneLabel: string;
    addressLabel: string;
    addressValue: string;
  };
  footer: {
    tag: string;
    explore: string;
    company: string;
    legal: string;
    contact: string;
    newsletter: string;
    subscribe: string;
    privacy: string;
    terms: string;
    safety: string;
    cookies: string;
    rights: string;
  };
  legal: {
    privacy: LegalPage;
    terms: LegalPage;
    "child-safety": LegalPage;
    cookies: LegalPage;
    notFoundTitle: string;
  };
  notFound: { title: string; desc: string; cta: string };
}

export const translations = {
  en: {
    brand: "Code Cubs Academy",
    slogan: "Where little coders become big creators",
    nav: {
      home: "Home",
      about: "About",
      courses: "Courses",
      path: "Learning Path",
      instructors: "Instructors",
      projects: "Projects",
      pricing: "Pricing",
      faq: "FAQ",
      contact: "Contact",
      login: "Login",
      signup: "Sign Up",
      blog: "Blog",
      events: "Events",
      resources: "Resources",
    },
    cta: {
      startLearning: "Start Learning",
      bookFree: "Book Free Session",
      explore: "Explore Courses",
      enroll: "Enroll Now",
      verify: "Verify Certificate",
      download: "Download Now",
    },
    resources: {
      title: "Free Resources & Activities",
      desc: "Educational materials and fun activities you can download and use with your child at home to make them love coding.",
      bookTitle: "Book: Journey to the World of Coding",
      bookDesc:
        "A simple and fun guide for kids explaining the basics of logical thinking and programming through exciting stories and challenges. Suitable for ages 6 to 12.",
      bookAuthor: "By: Code Cubs Academy Team",
      activitiesTitle: "Activities & Challenges",
      activitiesDesc: "Print them and let your child challenge themselves",
      activities: [
        {
          title: "Worksheet: Robot Maze",
          desc: "Use the arrows to help the robot reach the goal.",
        },
        { title: "Screen-free Coding Cards", desc: "Cards to teach logic away from screens." },
        {
          title: "Pixel Art Challenge",
          desc: "Color the squares by numbers to reveal a fun shape.",
        },
      ],
    },
    home: {
      heroEyebrow: "Coding for kids 6-16",
      heroDesc:
        "Fun, project-based coding classes that turn screen time into creative time. Live instructors, AI helpers, and real projects kids love to show off.",
      trustedBy: "Trusted by parents around the world",
      whyTitle: "Why parents choose Code Cubs",
      whyDesc: "We blend fun, structure, and real outcomes.",
      featuresList: [
        {
          t: "Live Expert Instructors",
          d: "Small group classes led by passionate teachers, never auto-graded videos.",
        },
        {
          t: "Project Based",
          d: "Kids build games, websites, apps and AI bots they can actually show off.",
        },
        {
          t: "Safe & Kid Friendly",
          d: "Parent dashboard, moderated chat, and zero ads. A safe place to learn.",
        },
        {
          t: "AI Coding Helper",
          d: "An on-demand AI buddy that explains code in kid-friendly language.",
        },
      ],
      coursesTitle: "Popular Courses",
      coursesDesc: "Hand-crafted tracks for every age and skill level.",
      projectsTitle: "Look what our cubs built!",
      projectsDesc: "Real student projects, no filters.",
      projectsList: [
        { emoji: "🐢", t: "Turtle Race", a: "Lina, 8" },
        { emoji: "👾", t: "Space Defender", a: "Omar, 11" },
        { emoji: "🎨", t: "Pixel Painter", a: "Sara, 9" },
        { emoji: "🧠", t: "Quiz Bot", a: "Adam, 13" },
      ],
      testimonialsTitle: "Loved by parents and kids",
      testimonialsList: [
        {
          name: "Mona K.",
          role: "Parent of Yusuf, 10",
          q: "My son went from playing games to building them. The instructors are amazing.",
        },
        {
          name: "Hassan A.",
          role: "Parent of Lina, 8",
          q: "Live classes, real teachers, and zero stress. Best investment we've made.",
        },
        {
          name: "Layla M.",
          role: "Student, 13",
          q: "I built my first website! My friends were so impressed.",
        },
      ],
      eventsTitle: "Upcoming Events & Camps",
      eventsList: [
        { date: "Jun 15", t: "Summer Coding Camp", d: "2 weeks, ages 8-12" },
        { date: "Jul 02", t: "Family Hack Day", d: "Build together at home" },
        { date: "Aug 20", t: "AI Workshop", d: "Train your first model" },
      ],
      faqTitle: "Frequently Asked Questions",
      ctaTitle: "Ready to raise a creator?",
      ctaDesc: "Book a free session and see your child light up.",
    },
    about: {
      title: "About Code Cubs Academy",
      lead: "We help kids fall in love with coding through projects that matter to them.",
      mission: "Our mission",
      missionText:
        "To prepare the next generation of creators with the skills, mindset, and confidence to build the future.",
      vision: "Our vision",
      visionText: "A world where every child has access to high-quality, joyful coding education.",
      stats: [
        { n: "5,000+", l: "Happy Students" },
        { n: "120+", l: "Expert Mentors" },
        { n: "30+", l: "Countries" },
        { n: "98%", l: "Parent Satisfaction" },
      ],
    },
    courses: {
      title: "Courses",
      desc: "Pick a track that fits your child's age and interests.",
      ageLabel: "Ages",
      durationLabel: "Duration",
      levelLabel: "Level",
      viewDetails: "View details",
      list: [
        {
          id: "scratch",
          title: "Scratch Game Studio",
          desc: "Drag-and-drop coding to build animated games and stories.",
          age: "6-9",
          duration: "8 weeks",
          level: "Beginner",
          color: "fun-yellow",
        },
        {
          id: "python",
          title: "Python Adventures",
          desc: "Real Python code to make turtle art, mini-games and quizzes.",
          age: "9-12",
          duration: "10 weeks",
          level: "Beginner",
          color: "fun-green",
        },
        {
          id: "web",
          title: "Web Wizards (HTML/CSS/JS)",
          desc: "Build and publish their first real website.",
          age: "10-14",
          duration: "12 weeks",
          level: "Intermediate",
          color: "fun-blue",
        },
        {
          id: "ai",
          title: "AI for Kids",
          desc: "Train mini AI models and chatbots in a safe sandbox.",
          age: "12-16",
          duration: "10 weeks",
          level: "Advanced",
          color: "fun-pink",
        },
        {
          id: "mobile",
          title: "App Inventors",
          desc: "Design and ship a real mobile app together.",
          age: "11-15",
          duration: "12 weeks",
          level: "Intermediate",
          color: "fun-orange",
        },
        {
          id: "roblox",
          title: "Roblox Lua Masters",
          desc: "Code real Roblox experiences using Lua scripting.",
          age: "10-14",
          duration: "10 weeks",
          level: "Intermediate",
          color: "fun-purple",
        },
      ],
    },
    path: {
      title: "Learning Path",
      desc: "A clear roadmap from first block to AI builder.",
      steps: [
        { n: "01", t: "Scratch", d: "Build logic intuition with block coding." },
        { n: "02", t: "Python", d: "Write real code, solve real problems." },
        { n: "03", t: "Web Dev", d: "Ship a website to the internet." },
        { n: "04", t: "AI & Beyond", d: "Train models and build smart apps." },
      ],
    },
    pricing: {
      title: "Simple, family-friendly plans",
      desc: "Cancel anytime. Sibling discount included.",
      monthly: "Monthly",
      term: "Per Term",
      yearly: "Yearly",
      perMonth: "/ month",
      popular: "Most popular",
      plans: [
        {
          name: "Starter",
          price: "$29",
          period: "/ month",
          features: ["1 live class / week", "Access to AI helper", "Parent dashboard"],
        },
        {
          name: "Pro",
          price: "$59",
          period: "/ month",
          features: [
            "2 live classes / week",
            "All courses access",
            "Projects review",
            "Certificates",
          ],
          popular: true,
        },
        {
          name: "Family",
          price: "$89",
          period: "/ month",
          features: ["Up to 3 kids", "All Pro features", "Sibling 20% off", "Priority support"],
        },
      ],
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          q: "What age is the academy for?",
          a: "We teach kids ages 6 to 16 with tracks tailored to each age range.",
        },
        {
          q: "Do I need any computer?",
          a: "Any laptop or desktop with Chrome works. No installs required.",
        },
        {
          q: "Are classes live or recorded?",
          a: "Classes are 100% live in small groups with expert instructors.",
        },
        {
          q: "What language are classes in?",
          a: "Classes are offered in both Arabic and English.",
        },
        { q: "Is there a free trial?", a: "Yes! Book a free 45-minute trial session." },
        {
          q: "Can my child get a certificate?",
          a: "Yes, every completed course earns a verifiable certificate.",
        },
      ],
    },
    contact: {
      title: "Get in touch",
      desc: "Questions? Want to book a free session? We'd love to hear from you.",
      name: "Parent's name",
      email: "Email",
      phone: "Phone",
      childAge: "Child's age",
      message: "Message",
      send: "Send message",
      sending: "Sending...",
      bookSession: "Book a Free Session",
      success: "Thanks! We'll get back to you within 24 hours.",
      emailLabel: "Email",
      phoneLabel: "Phone",
      addressLabel: "Address",
      addressValue: "Online — worldwide",
    },
    footer: {
      tag: "Where little coders become big creators.",
      explore: "Explore",
      company: "Company",
      legal: "Legal",
      contact: "Contact",
      newsletter: "Get our weekly tips for parents",
      subscribe: "Subscribe",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      safety: "Child Safety",
      cookies: "Cookies",
      rights: "All rights reserved.",
    },
    legal: {
      privacy: {
        title: "Privacy Policy",
        body: "We collect only the minimum data needed to run the academy. We never sell or share student data. Parents control all child accounts.",
      },
      terms: {
        title: "Terms of Service",
        body: "By using Code Cubs Academy you agree to use our platform respectfully and lawfully. Full terms available on request.",
      },
      "child-safety": {
        title: "Child Safety",
        body: "All instructors are background-checked. Live classes are monitored. Chat is moderated. Parents have full visibility.",
      },
      cookies: {
        title: "Cookies Policy",
        body: "We use essential cookies for login and analytics cookies to improve the experience. You can opt out anytime.",
      },
      notFoundTitle: "Not found",
    },
    notFound: {
      title: "Oops! This page got lost in cyberspace.",
      desc: "Let's get you back to coding.",
      cta: "Take me home",
    },
  },
  ar: {
    brand: "Code Cubs Academy",
    slogan: "هنا الـ coders الصغيرين بيبقوا creators كبار",
    nav: {
      home: "الرئيسية",
      about: "عننا",
      courses: "الكورسات",
      path: "Learning Path",
      instructors: "المدربين",
      projects: "المشاريع",
      pricing: "الأسعار",
      faq: "أسئلة شائعة",
      contact: "كلّمنا",
      login: "Login",
      signup: "Sign Up",
      blog: "المدونة",
      events: "الفعاليات",
      resources: "المصادر",
    },
    cta: {
      startLearning: "ابدأ Learning",
      bookFree: "احجز Free Session",
      explore: "اكتشف الكورسات",
      enroll: "سجّل دلوقتي",
      verify: "تحقق من الـ Certificate",
      download: "تحميل الآن",
    },
    resources: {
      title: "مصادر وأنشطة مجانية",
      desc: "مواد تعليمية وأنشطة ممتعة تقدر تحمّلها وتستخدمها مع طفلك في البيت عشان تحببه في البرمجة.",
      bookTitle: "كتاب: رحلة إلى عالم البرمجة",
      bookDesc:
        "دليل مبسط وممتع للأطفال يشرح أساسيات التفكير المنطقي والبرمجة من خلال قصص شيقة وتحديات ممتعة. مناسب من سن 6 لـ 12 سنة.",
      bookAuthor: "إعداد: فريق Code Cubs Academy",
      activitiesTitle: "أنشطة وتحديات",
      activitiesDesc: "اطبعها وخلّي طفلك يتحدى نفسه",
      activities: [
        {
          title: "ورقة عمل: متاهة الروبوت",
          desc: "استخدم الأسهم لمساعدة الروبوت في الوصول للهدف.",
        },
        { title: "بطاقات البرمجة بدون شاشة", desc: "بطاقات لتعليم الـ Logic بعيداً عن الشاشات." },
        {
          title: "تحدي تلوين البكسل (Pixel Art)",
          desc: "لوّن المربعات حسب الأرقام لتكوين شكل ممتع.",
        },
      ],
    },
    home: {
      heroEyebrow: "Coding للأطفال من 6 لـ 16 سنة",
      heroDesc:
        "كلاسات coding ممتعة و project-based، بتحوّل وقت الشاشة لوقت إبداع. Live instructors، AI helpers، و projects حقيقية الأطفال بتفتخر بيها.",
      trustedBy: "آلاف من الأهالي حول العالم بيثقوا فينا",
      whyTitle: "ليه الأهالي بيختاروا Code Cubs؟",
      whyDesc: "بنخلط بين المتعة، الـ structure، و results حقيقية.",
      featuresList: [
        {
          t: "Live Expert Instructors",
          d: "كلاسات small-group مع مدرسين شغوفين — مش مجرد فيديوهات auto-graded.",
        },
        {
          t: "Project-Based Learning",
          d: "الأطفال بيبنوا games و websites و apps و AI bots يقدروا يعرضوها.",
        },
        {
          t: "Safe & Kid-Friendly",
          d: "Parent dashboard، chat متفلتر، و zero ads. مكان آمن للتعلم.",
        },
        { t: "AI Coding Helper", d: "AI buddy بيشرح الـ code بلغة بسيطة للأطفال في أي وقت." },
      ],
      coursesTitle: "أشهر الـ Courses",
      coursesDesc: "Tracks مصممة لكل عمر و skill level.",
      projectsTitle: "شوف الـ cubs بنوا إيه!",
      projectsDesc: "Real projects من طلابنا، من غير فلاتر.",
      projectsList: [
        { emoji: "🐢", t: "Turtle Race", a: "لينا، 8 سنين" },
        { emoji: "👾", t: "Space Defender", a: "عمر، 11 سنة" },
        { emoji: "🎨", t: "Pixel Painter", a: "سارة، 9 سنين" },
        { emoji: "🧠", t: "Quiz Bot", a: "آدم، 13 سنة" },
      ],
      testimonialsTitle: "الأهالي و الأطفال بيحبونا",
      testimonialsList: [
        {
          name: "منى ك.",
          role: "والدة يوسف، 10 سنين",
          q: "ابني كان بيلعب games، دلوقتي بيـ build games بنفسه. الـ instructors رهيبين.",
        },
        {
          name: "حسن ع.",
          role: "والد لينا، 8 سنين",
          q: "Live classes، مدرسين حقيقيين، و zero ضغط. أحسن investment عملناه.",
        },
        { name: "ليلى م.", role: "Student, 13", q: "عملت أول website ليا! صحابي اتبهروا جامد." },
      ],
      eventsTitle: "Events و Camps جاية",
      eventsList: [
        { date: "15 يونيو", t: "Summer Coding Camp", d: "أسبوعين، من 8 لـ 12 سنة" },
        { date: "2 يوليو", t: "Family Hack Day", d: "ابنوا مع بعض من البيت" },
        { date: "20 أغسطس", t: "AI Workshop", d: "درّب أول model ليك" },
      ],
      faqTitle: "أسئلة شائعة",
      ctaTitle: "جاهز تربّي creator؟",
      ctaDesc: "احجز free session و شوف عيون ابنك بتلمع.",
    },
    about: {
      title: "عن Code Cubs Academy",
      lead: "بنخلي الأطفال يحبوا الـ coding من خلال projects تهمّهم فعلاً.",
      mission: "Mission بتاعتنا",
      missionText: "إعداد جيل جديد من الـ creators بالمهارات و الـ mindset و الثقة لبناء المستقبل.",
      vision: "Vision بتاعتنا",
      visionText: "عالم كل طفل فيه يقدر يوصل لـ coding education ممتعة و عالية الجودة.",
      stats: [
        { n: "+5,000", l: "Happy Students" },
        { n: "+120", l: "Expert Mentors" },
        { n: "+30", l: "دولة" },
        { n: "98%", l: "رضا الأهالي" },
      ],
    },
    courses: {
      title: "الكورسات",
      desc: "اختار الـ track المناسب لعمر ابنك و اهتماماته.",
      ageLabel: "العمر",
      durationLabel: "المدة",
      levelLabel: "Level",
      viewDetails: "View details",
      list: [
        {
          id: "scratch",
          title: "Scratch Game Studio",
          desc: "Drag-and-drop coding لبناء games و قصص متحركة.",
          age: "6-9",
          duration: "8 أسابيع",
          level: "Beginner",
          color: "fun-yellow",
        },
        {
          id: "python",
          title: "Python Adventures",
          desc: "Python code حقيقي لـ turtle art و mini-games و quizzes.",
          age: "9-12",
          duration: "10 أسابيع",
          level: "Beginner",
          color: "fun-green",
        },
        {
          id: "web",
          title: "Web Wizards (HTML/CSS/JS)",
          desc: "ابني و انشر أول website ليك.",
          age: "10-14",
          duration: "12 أسبوع",
          level: "Intermediate",
          color: "fun-blue",
        },
        {
          id: "ai",
          title: "AI for Kids",
          desc: "درّب mini AI models و chatbots في sandbox آمن.",
          age: "12-16",
          duration: "10 أسابيع",
          level: "Advanced",
          color: "fun-pink",
        },
        {
          id: "mobile",
          title: "App Inventors",
          desc: "صمّم و اطلق mobile app حقيقي مع بعض.",
          age: "11-15",
          duration: "12 أسبوع",
          level: "Intermediate",
          color: "fun-orange",
        },
        {
          id: "roblox",
          title: "Roblox Lua Masters",
          desc: "اعمل Roblox experiences حقيقية بالـ Lua scripting.",
          age: "10-14",
          duration: "10 أسابيع",
          level: "Intermediate",
          color: "fun-purple",
        },
      ],
    },
    path: {
      title: "Learning Path",
      desc: "Roadmap واضحة من أول block لحد AI builder.",
      steps: [
        { n: "01", t: "Scratch", d: "ابني logic intuition بالـ block coding." },
        { n: "02", t: "Python", d: "اكتب real code و حل real problems." },
        { n: "03", t: "Web Dev", d: "انشر أول website على الإنترنت." },
        { n: "04", t: "AI & Beyond", d: "درّب models و ابني smart apps." },
      ],
    },
    pricing: {
      title: "خطط بسيطة و family-friendly",
      desc: "Cancel في أي وقت. Sibling discount متضمن.",
      monthly: "Monthly",
      term: "Per Term",
      yearly: "Yearly",
      perMonth: "/ شهرياً",
      popular: "الأكثر شيوعاً",
      plans: [
        {
          name: "Starter",
          price: "$29",
          period: "/ شهرياً",
          features: ["Live class واحدة في الأسبوع", "AI helper متاح", "Parent dashboard"],
        },
        {
          name: "Pro",
          price: "$59",
          period: "/ شهرياً",
          features: [
            "2 live classes في الأسبوع",
            "كل الـ courses",
            "Projects review",
            "Certificates",
          ],
          popular: true,
        },
        {
          name: "Family",
          price: "$89",
          period: "/ شهرياً",
          features: ["لحد 3 أطفال", "كل مزايا Pro", "خصم 20% للإخوة", "Priority support"],
        },
      ],
    },
    faq: {
      title: "أسئلة شائعة",
      items: [
        { q: "الأكاديمية لأي عمر؟", a: "بنعلّم الأطفال من 6 لـ 16 سنة بـ tracks متفصّلة لكل فئة." },
        { q: "محتاج جهاز معين؟", a: "أي laptop أو desktop فيه Chrome يكفي. مفيش installs." },
        {
          q: "الـ classes live و لا recorded؟",
          a: "كل الكلاسات 100% live في small groups مع expert instructors.",
        },
        { q: "الـ classes بأي لغة؟", a: "متاحة بالعربية و الإنجليزية." },
        { q: "في free trial؟", a: "أيوه! احجز free trial session لمدة 45 دقيقة." },
        {
          q: "ابني هياخد certificate؟",
          a: "أيوه، كل course بيتكمل بياخد certificate قابل للـ verify.",
        },
      ],
    },
    contact: {
      title: "كلّمنا",
      desc: "عندك سؤال؟ عايز تحجز free session؟ يسعدنا نسمع منك.",
      name: "اسم ولي الأمر",
      email: "Email",
      phone: "Phone",
      childAge: "عمر الطفل",
      message: "Message",
      send: "Send Message",
      sending: "بنبعت...",
      bookSession: "احجز Free Session",
      success: "شكراً! هنرد عليك خلال 24 ساعة.",
      emailLabel: "Email",
      phoneLabel: "Phone",
      addressLabel: "العنوان",
      addressValue: "Online — حول العالم",
    },
    footer: {
      tag: "هنا الـ coders الصغيرين بيبقوا creators كبار.",
      explore: "اكتشف",
      company: "الشركة",
      legal: "Legal",
      contact: "Contact",
      newsletter: "Tips أسبوعية للأهالي",
      subscribe: "Subscribe",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      safety: "Child Safety",
      cookies: "Cookies",
      rights: "كل الحقوق محفوظة.",
    },
    legal: {
      privacy: {
        title: "Privacy Policy",
        body: "بنجمع أقل data ممكنة لتشغيل الأكاديمية. عمرنا ما هنبيع أو نشارك student data. ولي الأمر متحكم في كل accounts الأطفال.",
      },
      terms: {
        title: "Terms of Service",
        body: "باستخدامك Code Cubs Academy، إنت موافق إنك تستخدم الـ platform باحترام و في إطار القانون. الـ full terms متاحة عند الطلب.",
      },
      "child-safety": {
        title: "Child Safety",
        body: "كل الـ instructors متحقَّق منهم. الـ live classes تحت المراقبة. الـ chat متفلتر. ولي الأمر شايف كل حاجة.",
      },
      cookies: {
        title: "Cookies Policy",
        body: "بنستخدم essential cookies للـ login و analytics cookies لتحسين التجربة. تقدر تـ opt out في أي وقت.",
      },
      notFoundTitle: "مش موجودة",
    },
    notFound: {
      title: "Oops! الصفحة دي تايهة في الـ cyberspace.",
      desc: "خلينا نرجّعك تكمّل coding.",
      cta: "ودّيني للـ Home",
    },
  },
} satisfies Record<Locale, Dict>;
